const isFiniteDate = (value: unknown): value is Date =>
  value instanceof Date && Number.isFinite(value.getTime());

const emptyOnRangeError = (error: unknown): string => {
  if (error instanceof RangeError) return "";
  throw error;
};

const installDateTimeFormatGuard = () => {
  const prototype = Intl.DateTimeFormat.prototype;
  const descriptor = Object.getOwnPropertyDescriptor(prototype, "format");
  const originalGet = descriptor?.get;
  if (!originalGet || !descriptor.configurable) return;

  Object.defineProperty(prototype, "format", {
    configurable: true,
    get() {
      const format = originalGet.call(this) as (date?: Date) => string;
      return (date?: Date) => {
        if (!isFiniteDate(date)) return "";
        try {
          return format(date);
        } catch (error) {
          return emptyOnRangeError(error);
        }
      };
    },
  });
};

const installLocaleGuard = (
  methodName: "toLocaleString" | "toLocaleDateString" | "toLocaleTimeString",
) => {
  const original = Date.prototype[methodName];
  // Safari throws from these methods when the date is not finite.
  // eslint-disable-next-line no-extend-native
  Date.prototype[methodName] = function (
    this: Date,
    ...args: Parameters<typeof original>
  ) {
    if (!Number.isFinite(this.getTime())) return "";
    try {
      return original.apply(this, args);
    } catch (error) {
      return emptyOnRangeError(error);
    }
  };
};

installDateTimeFormatGuard();
installLocaleGuard("toLocaleString");
installLocaleGuard("toLocaleDateString");
installLocaleGuard("toLocaleTimeString");
